import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Download, Printer, Settings2 } from 'lucide-react'
import { getBalanceSheetGaap } from '../../api/accounting'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { StatusBadge } from '../../components/PageParts'
import { fmtDay, localToday } from '../../utils/format'
import { ReportsTabs, StatementHead, StatementTable, acct, balanceRows, exportRowsCsv, useCompanyName } from './reportsShared'
import { printPage, useAutoPrint } from '../../utils/print'

const COMPARE = [
  { id: 'year_end', label: 'End of last year' },
  { id: 'month_end', label: 'End of last month' },
  { id: 'none', label: 'No comparison' },
]

// Classified balance sheet (GAAP): current and non-current assets, current
// and long-term liabilities, and equity, with the balance check.
export default function BalanceSheet() {
  const company = useCompanyName()
  const [params, setParams] = useSearchParams()
  const asOf = params.get('as_of') || localToday()
  const compare = COMPARE.some((c) => c.id === params.get('compare')) ? params.get('compare') : 'year_end'
  const [data, setData] = useState(null)
  useAutoPrint(!!data)
  const [error, setError] = useState('')
  const [openAll, setOpenAll] = useState(false)
  const set = (patch) => setParams((p) => { const n = new URLSearchParams(p); Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); return n }, { replace: true })

  useEffect(() => {
    setError('')
    setData(null)
    getBalanceSheetGaap({ as_of: asOf, compare })
      .then(setData)
      .catch((e) => setError(e.message || 'Could not load the balance sheet.'))
  }, [asOf, compare])

  const rows = useMemo(() => (data ? balanceRows(data) : []), [data])

  const priorLabel = data?.prior_as_of ? fmtDay(data.prior_as_of) : ''
  const currentLabel = data ? fmtDay(data.as_of) : ''

  return (
    <div className="ci-page fs-page">
      <ReportsTabs />
      <div className="ci-header-row fs-noprint">
        <div className="ci-title-block">
          <div>
            <h1>Balance sheet <HelpTip k="balanceSheet" /></h1>
            <div className="ci-title-meta">What the business owns and owes on a date.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => exportRowsCsv(rows, { name: 'balance_sheet', currentLabel: asOf, priorLabel: data?.prior_as_of || '' })}><Download size={15} />Export</button>
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => printPage()}><Printer size={15} />Print</button>
        </div>
      </div>

      <div className="ci-toolbar fs-noprint">
        <label className="fs-inline">As of <input className="ci-select" type="date" value={asOf} onChange={(e) => set({ as_of: e.target.value })} /></label>
        <select className="ci-select" value={compare} onChange={(e) => set({ compare: e.target.value })} aria-label="Compare with">
          {COMPARE.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setOpenAll((v) => !v)}>{openAll ? 'Hide accounts' : 'Show accounts'}</button>
      </div>

      {error && <ErrorBanner message={error} />}

      {!data ? (!error && <Spinner label="Preparing the balance sheet…" />) : (
        <section className="fs-card">
          <StatementHead company={company} title="Balance sheet" sub={`As of ${fmtDay(data.as_of)}`} />
          <div className="fs-check fs-noprint">
            {data.balanced
              ? <StatusBadge label="Assets = liabilities + equity" tone="good" />
              : <StatusBadge label={`Out of balance by ${acct(Math.abs(data.difference || data.prior_difference))}`} tone="bad" />}
            <HelpTip k="balanceCheck" />
          </div>
          <StatementTable key={`${asOf}-${compare}`} rows={rows} currentLabel={currentLabel} priorLabel={priorLabel} openAll={openAll} glRange={[null, asOf]} />
          <p className="fs-foot fs-noprint">Accounts are placed in sections automatically. <Link to="/accounting/reports/classification"><Settings2 size={12} aria-hidden="true" /> Account classification</Link></p>
        </section>
      )}
    </div>
  )
}
